/**
 * @module scoreboardUtils
 * @description Shared formatting helpers for scoreboard turn display.
 * Used by both ScoreBoard and Summary components to ensure consistent
 * turn description wording.
 */

/**
 * Formats a Turn object into a human-readable description string.
 * Used by Summary (full text) and as tooltip text in ScoreBoard.
 *
 * @param {object|null|undefined} turn - A Turn object from game.scoreBoard.rounds[n].turns[n]
 * @param {string} turn.type - "word", "pass", or "exchange"
 * @param {string[]} turn.wordsPlayed - Words formed (primary word at index 0)
 * @param {number} turn.turnScore - Points scored on this turn
 * @returns {string} Formatted description, e.g. "Played SCOUR for 15 pts", "Passed", "Exchanged", or ""
 */
export function formatTurnDescription(turn) {
    if (!turn || !turn.type) {
        return "";
    }

    switch (turn.type) {
        case "word":
            return `Played ${turn.wordsPlayed[0]} for ${turn.turnScore} pts`;
        case "pass":
            return "Passed";
        case "exchange":
            return "Exchanged";
        case "challenged":
            return "Play challenged (0 pts)";
        case "challenge":
            return "Challenge failed (0 pts)";
        case "finalScore":
            return turn.turnScore >= 0
                ? `Final Adjustment +${turn.turnScore}`
                : `Final Adjustment ${turn.turnScore}`;
        default:
            return "";
    }
}

/**
 * Returns the compact display value for a ScoreBoard cell.
 * Word turns show the numeric turn score; pass/exchange show a short label.
 *
 * @param {object|null|undefined} turn - A Turn object from game.scoreBoard.rounds[n].turns[n]
 * @returns {string} Compact label: the turn score (as string), "PASS", "EXCHANGE", or ""
 */
export function formatTurnCell(turn) {
    if (!turn || !turn.type) {
        return "";
    }

    switch (turn.type) {
        case "word":
            return String(turn.turnScore);
        case "pass":
            return "PASS";
        case "exchange":
            return "EXCHANGE";
        case "challenged":
            return "CHLGD";
        case "challenge":
            return "CHLG";
        case "finalScore":
            return turn.turnScore >= 0 ? `+${turn.turnScore}` : String(turn.turnScore);
        default:
            return "";
    }
}

/**
 * Formats the last completed turn into a human-readable activity summary,
 * including the player's name.
 * Used by Summary to populate the last-activity heading line.
 *
 * @param {object|null|undefined} turn - A Turn object from game.scoreBoard.rounds[n].turns[n]
 * @param {string|null|undefined} playerName - The display name of the player who took the turn
 * @returns {string} e.g. "Alice played SCOUR for 15 pts", "Bob passed", or "No activity yet"
 */
export function formatLastActivity(turn, playerName) {
    if (!turn || !turn.type || !playerName) return "No activity yet";
    switch (turn.type) {
        case "word":     return `${playerName} played ${turn.wordsPlayed[0]} for ${turn.turnScore} pts`;
        case "pass":     return `${playerName} passed`;
        case "exchange": return `${playerName} exchanged`;
        case "challenged": return `${playerName}'s play was challenged`;
        case "challenge":  return `${playerName} challenged (no score)`;
        case "finalScore": return turn.turnScore >= 0
            ? `${playerName} finished with +${turn.turnScore} pts`
            : `${playerName} finished with ${turn.turnScore} pts`;
        default:         return "No activity yet";
    }
}
